import { Fragment } from 'react';
import { When, If, Then, Else } from 'react-if';
import { FilterDefn } from '../../index';
import { useFilter } from '../../hooks';
import { FilterChecklist } from '../FilterChecklist';
import styled from 'styled-components6';
import { keyForOption } from '../utils';
import { Body1 } from '../../../../theme';

type NestedMultiListProps = {
    options: FilterDefn[];
};

const Container = styled.div`
    margin-left: var(--rwl-spacing-md);
`;

const GroupTitle = styled(Body1)`
    && {
        font-weight: 700;
        margin-left: var(--rwl-spacing-xs);
    }
`;

export const NestedMultiList = ({
    options,
}: NestedMultiListProps): JSX.Element => {
    const { appliedFilters } = useFilter();

    return (
        <div className="FilterNestedMultiList">
            {options.map((option) => {
                const {
                    displayOnly,
                    isChecked,
                    hasSubFilters,
                    renderInIndeterminateState,
                    isLastLayer,
                } = getRenderingOptions(option, appliedFilters);

                return (
                    <Fragment key={keyForOption(option)}>
                        <If condition={!displayOnly}>
                            <Then>
                                <FilterChecklist
                                    option={option}
                                    indeterminate={renderInIndeterminateState}
                                />
                            </Then>
                            <Else>
                                <GroupTitle
                                    className={'FilterNestedMultiListItemTitle'}
                                >
                                    {option.title === 'Service Type'
                                        ? 'Type'
                                        : option.title}
                                </GroupTitle>
                            </Else>
                        </If>
                        <When
                            condition={
                                displayOnly || (isChecked && hasSubFilters)
                            }
                        >
                            <Container
                                className={
                                    isLastLayer
                                        ? 'FilterNestedMultiListSubFiltersLast'
                                        : 'FilterNestedMultiListSubFilters'
                                }
                            >
                                <NestedMultiList
                                    options={option?.subFilters || []}
                                />
                            </Container>
                        </When>
                    </Fragment>
                );
            })}
        </div>
    );
};

function getRenderingOptions(option: FilterDefn, appliedFilters: string[]) {
    const isChecked = appliedFilters.includes(option.value);
    const numberOfSubFilters = option.subFilters?.length || 0;
    const hasSubFilters = numberOfSubFilters > 0;
    const subFilters = option.subFilters || [];
    const subFilterChildrenCount = subFilters.reduce((acc, subFilter) => {
        return acc + (subFilter?.subFilters?.length || 0);
    }, 0);
    const isLastLayer = subFilterChildrenCount === 0;
    const numberOfCheckedSubFilters =
        subFilters.filter((subFilter) =>
            appliedFilters.includes(subFilter.value)
        ).length || 0;
    const numberOfCheckedSubFilterChildren = subFilters.reduce(
        (acc, subFilter) => {
            const subFilterChildren = subFilter.subFilters || [];
            subFilterChildren.forEach((subFilterChild) => {
                acc += appliedFilters.includes(subFilterChild.value) ? 1 : 0;
            });
            return acc;
        },
        0
    );
    const renderInIndeterminateState =
        hasSubFilters &&
        (numberOfCheckedSubFilters >= 1 ||
            numberOfCheckedSubFilterChildren >= 1);

    return {
        displayOnly: option.displayOnly,
        isChecked,
        hasSubFilters,
        renderInIndeterminateState,
        isLastLayer,
    };
}
